import {ChangeDetectionStrategy, Component, computed, effect, inject, Signal, signal} from '@angular/core';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
  AttentionPoint,
  AttentionPointContext,
  Clearance,
  ClearanceStatus,
  EmergencyProcedureMutation,
  PilotFromDistanceRequestStatus,
  RequiredQuantity,
  SaveEmergencyProcedureMutations,
  TaskMessageStatus,
} from "@portbase/bezoekschip-service-typescriptmodels";
import {closeEditModal, sendCommandAndWait} from "../../../common/utils";
import {ClearanceService} from "../../../visit-details/visit/clearance/clearance-service";
import {publishHandlerEvent} from "../../../common/handlers/app-common-utils";
import {AppContext} from "../../../app-context";
import {
  EmergencySummaryExpanded,
  isAccessPoint,
  isBerth
} from "../emergency-overview-item/emergency-overview-item.component";
import {EmergencyOverviewUtils} from "../emergency-overview.utils";

type AttentionPointGroup = FormGroup<{
  type: FormControl<string | null>;
  remarks: FormControl<string | null>;
}>;

export function createAttentionPointGroup(type: string | null, remarks: string | null): AttentionPointGroup {
  return new FormGroup({
    type: new FormControl(type),
    remarks: new FormControl(remarks),
  });
}

function createAttentionPointsArray(): FormArray<AttentionPointGroup> {
  return new FormArray<AttentionPointGroup>([]);
}

@Component({
    selector: 'app-operational-visit-editor',
    templateUrl: './operational-visit-editor.component.html',
    styleUrl: './operational-visit-editor.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class OperationalVisitEditorComponent {

  protected readonly _data = signal<OperationalVisitEditorComponentData | undefined>(undefined);
  protected readonly summary: Signal<EmergencySummaryExpanded>  = computed(() => this._data().summary)
  // Incoming trip = travelling to the first berth, i.e. departing from an access point (entry point).
  protected readonly isIncomingTrip: Signal<boolean> = computed(() => isAccessPoint(this.summary().movementOriginPlace) && isBerth(this.summary().movementTargetPlace));
  protected readonly isOutgoingTrip: Signal<boolean> = computed(() => isBerth(this.summary().movementOriginPlace) && isAccessPoint(this.summary().movementTargetPlace));
  // Pure LW (pilot) editor: may open the editor but only edit the LW-allowed fields. The MH-only controls
  // in the sub-editors are hidden when this is true. Mirrors the backend pure-LW write filter.
  protected readonly isPilotOnly: Signal<boolean> = computed(() => AppContext.isPilotEmergencyProcedureViewerOnly());

  private readonly clearanceService = inject(ClearanceService);

  private initialFormValue: EmergencyProcedureMutation;

  // Form structure mirrors the EmergencyProcedureMutation type:
  //   { berth: BerthMutations, portAuthority: PortAuthorityMutations,
  //     portVisit: PortVisitMutations, services: ServicesMutations }
  protected readonly form = new FormGroup({
    portAuthority: new FormGroup({
      clearances: new FormGroup({
        sspi: new FormGroup({
          status: new FormControl(),
          remarks: new FormControl(),
          attentionPoints: createAttentionPointsArray()
        }),
        emas: new FormGroup({
          status: new FormControl(),
          remarks: new FormControl(),
          attentionPoints: createAttentionPointsArray()
        }),
        emgs: new FormGroup({
          status: new FormControl(),
          remarks: new FormControl(),
          attentionPoints: createAttentionPointsArray()
        }),
        mdoh: new FormGroup({
          status: new FormControl(),
          remarks: new FormControl(),
          attentionPoints: createAttentionPointsArray()
        }),
        ekh: new FormGroup({
          status: new FormControl(),
          remarks: new FormControl(),
          attentionPoints: createAttentionPointsArray()
        })
      }),
      operationalContact: new FormControl(false),
      actualVesselDraft: new FormControl(),
      tidalWindow: new FormControl(false),
      tidalWindowStart: new FormControl(),
      tidalWindowEnd: new FormControl(),
      pilotFromDistance: new FormControl(false),
      pilotFromDistanceAccepted: new FormControl(false),
      onHold: new FormControl(false),
      onHoldDate: new FormControl()
    }),
    portVisit: new FormGroup({
      atdPort: new FormControl(),
      etaPbp: new FormControl(),
      etdPort: new FormControl(),
      pbp: new FormControl()
    }),
    services: new FormGroup({
      pilot: new FormControl(false),
      pilotApproval: new FormControl(),
      tugboat: new FormControl(false),
      tugboatOrganisation: new FormControl(),
      numberOfTugboats: new FormControl(),
      boatmen: new FormControl(false),
      boatmenOrganisation: new FormControl()
    }),
    berth: new FormGroup({
      code: new FormControl(),
      // name is a UI-only helper (resolved berth label); it is not part of BerthMutations.
      name: new FormControl(),
      ata: new FormControl(),
      atd: new FormControl(),
      eta: new FormControl(),
      etd: new FormControl(),
      bollardFrom: new FormControl(),
      bollardTo: new FormControl(),
      mooringPosition: new FormControl()
    })
  });

  set data(data: OperationalVisitEditorComponentData) {
    this._data.set(data);
  }

  constructor() {
    effect(() => {
      if (this.summary()) {
        this.initializeForm(
          this.summary(),
        );
      }
    });
  }

  private initializeForm(summary: EmergencySummaryExpanded): void {
    const numberOfTugBoats = this.getNumberOfTugboats(summary)
    const boatmenService = EmergencyOverviewUtils.getBoatmenService(summary);

    // On-hold boolean comes from the harbour-master info on the visit: the incoming movement for an
    // incoming trip, otherwise the target berth's movement. Falls back to false when absent.
    const target = summary.movementTargetPlace;
    let onHold = false;
    if (this.isIncomingTrip()) {
      onHold = summary.incomingMovementHarbourMasterInfo?.movementOnHold ?? false;
    } else if (isBerth(target)) {
      onHold = target.harbourMasterInfo?.movementOnHold ?? false;
    }

    // Fields synchronized to the visit should be comming from the visit or berthvisit.
    // Everything that is not synced back should be comming from the emergency procedure mutations
    this.form.patchValue({
      portAuthority: {
        operationalContact:summary.incomingOperationalContact,
        actualVesselDraft: EmergencyOverviewUtils.getActualVesselDraught(summary),
        tidalWindow: !!EmergencyOverviewUtils.getTidalWindow(summary),
        tidalWindowStart: EmergencyOverviewUtils.getTidalWindow(summary)?.start ?? null,
        tidalWindowEnd: EmergencyOverviewUtils.getTidalWindow(summary)?.end ?? null,
        pilotFromDistance: EmergencyOverviewUtils.isShoreBasedPilotage(summary),
        pilotFromDistanceAccepted:
          EmergencyOverviewUtils.getPilotFromDistanceStatus(summary) === PilotFromDistanceRequestStatus.ACCEPTED,
        onHold: onHold,
        onHoldDate: summary.onHoldDate ?? null
      },
      portVisit: {
        atdPort: summary.atdPort ?? null,
        etaPbp: summary.etaPbp ?? null,
        etdPort: summary.etdPort ?? null,
        pbp: summary.entryPoint?.code ?? null,
      },
    });

    this.form.patchValue({
      services: {
        boatmen: boatmenService?.required ?? false,
        boatmenOrganisation: boatmenService?.serviceProvider?.name ?? null,
      }
    })

    if (isBerth(summary.movementTargetPlace)) {
      this.form.patchValue({
        services: {
          pilot: summary.movementTargetPlace.ordered, // TODO: Right property?
          pilotApproval: TaskMessageStatus.UNKNOWN,
          tugboat: numberOfTugBoats !== null,
          // tugboatOrganisation: currentBerthVisit.tugboatAtArrival?.serviceProvider?.name
          //   ?? currentBerthVisit.tugboatAtDeparture?.serviceProvider?.name,
          numberOfTugboats: numberOfTugBoats,
        },
        berth: {
          code: summary.movementTargetPlace.code ?? null,
          name: summary.movementTargetPlace.name ?? null,
          ata: summary.movementTargetPlace.ata ?? null,
          atd: summary.movementTargetPlace.atd ?? null,
          eta: summary.movementTargetPlace.eta ?? null,
          etd: summary.movementTargetPlace.etd ?? null,
          bollardFrom: summary.movementTargetPlace.bollardFrom ?? null,
          bollardTo: summary.movementTargetPlace.bollardTo ?? null,
          mooringPosition: summary.movementTargetPlace.mooring ?? null
        }
      });
    } else {
      // What if target is exit point
    }

    const ekhMovement = summary.movementForEkhClearance;
    const ekh = ekhMovement
      ? this.clearanceService.getClearance(summary.clearances, AttentionPointContext.ekh, ekhMovement.to, ekhMovement.from)
      : null;
    const sspi = this.clearanceService.getClearance(summary.clearances, AttentionPointContext.sspi);
    const emas = this.clearanceService.getClearance(summary.clearances, AttentionPointContext.emas);
    const emgs = this.clearanceService.getClearance(summary.clearances, AttentionPointContext.emgs);
    const mdoh = this.clearanceService.getClearance(summary.clearances, AttentionPointContext.mdoh);

    const toClearanceValue = (clearance: Clearance | null) => ({
      status: clearance?.status ?? ClearanceStatus.PENDING,
      remarks: clearance?.remarks ?? null,
    });

    this.form.patchValue({
      portAuthority: {
        clearances: {
          sspi: toClearanceValue(sspi),
          emas: toClearanceValue(emas),
          emgs: toClearanceValue(emgs),
          mdoh: toClearanceValue(mdoh),
          ekh: toClearanceValue(ekh),
        },
      },
    });

    this.seedAttentionPoints('sspi', sspi?.attentionPoints);
    this.seedAttentionPoints('emas', emas?.attentionPoints);
    this.seedAttentionPoints('emgs', emgs?.attentionPoints);
    this.seedAttentionPoints('mdoh', mdoh?.attentionPoints);
    this.seedAttentionPoints('ekh', ekh?.attentionPoints);

    this.initialFormValue = structuredClone(this.form.value);
  }

  private seedAttentionPoints(key: string, attentionPoints: AttentionPoint[] | undefined): void {
    const array = this.form.get(`portAuthority.clearances.${key}.attentionPoints`) as unknown as FormArray<AttentionPointGroup>;
    array.clear();
    for (const ap of attentionPoints ?? []) {
      array.push(createAttentionPointGroup(ap.type ?? null, ap.remarks ?? null));
    }
  }

  cancel(): void {
    closeEditModal();
  }

  save(): void {
    const command: SaveEmergencyProcedureMutations = {
      crn: this.summary().crn,
      movementTarget: this.summary().movementTarget,
      mutations: this.getChangedValues(this.form, this.initialFormValue)
    };

    sendCommandAndWait(
      'com.portbase.bezoekschip.common.api.emergencyprocedure.SaveEmergencyProcedureMutations',
      command
    ).subscribe(() => {
      AppContext.registerSuccess('Emergency procedure mutations saved successfully.');
      // Pass the saved mutation along so the overview can apply it to its cached copy directly,
      // instead of depending on a round-trip refetch of the summary.
      publishHandlerEvent('emergencyProcedureMutationsSaved', {
        crn: this.summary().crn,
        movementTarget: this.summary().movementTarget,
        mutations: this.getChangedValues(this.form, this.initialFormValue),
      });
      closeEditModal();
    });
  }

  private getChangedValues(group: FormGroup, initial: EmergencyProcedureMutation): EmergencyProcedureMutation {
    const changed: EmergencyProcedureMutation = {};
    for (const [key, control] of Object.entries(group.controls)) {
      if (control instanceof FormGroup) {
        const nested = this.getChangedValues(control, initial?.[key] ?? {});
        if (Object.keys(nested).length > 0) {
          changed[key] = nested;
        }
      } else if (control instanceof FormArray) {
        if (control.dirty) {
          changed[key] = control.value;
        }
      } else if (control.dirty && control.value !== initial?.[key]) {
        changed[key] = control.value;
      }
    }

    // Clearances-only special case: whenever ANY field within one of the five clearance sub-groups
    // (portAuthority.clearances.{sspi|emas|emgs|mdoh|ekh}) changed, the emitted mutation for that
    // clearance MUST carry its `status` — even if the user only edited `remarks`. WHY: the backend
    // requires `status` on a clearance mutation to decide/dispatch the matching
    // Register*ClearanceCommand; a remarks-only edit without `status` is dropped on the backend.
    // The generic walk above is left untouched; we augment its result here, so BOTH getChangedValues
    // call sites in save() (command + publishHandlerEvent payload) get the corrected behaviour.
    // Clearance keys absent from `changed` (untouched clearances) are NOT created, which preserves
    // the no-spurious-mutation baseline for an unedited drawer.
    const clearances = changed['portAuthority']?.['clearances'];
    if (clearances) {
      for (const key of ['sspi', 'emas', 'emgs', 'mdoh', 'ekh']) {
        const clearance = clearances[key];
        if (!clearance || typeof clearance !== 'object') {
          continue;
        }
        if (!('status' in clearance)) {
          clearance.status = group.get(`portAuthority.clearances.${key}.status`)?.value;
        }

        const attentionPointsArray = group.get(`portAuthority.clearances.${key}.attentionPoints`) as FormArray<AttentionPointGroup>;
        clearance.attentionPoints = attentionPointsArray.controls
          .map(rowGroup => ({
            type: rowGroup.get('type')?.value,
            remarks: rowGroup.get('remarks')?.value,
          }))
          .filter(ap => !!ap.type);

        if (clearance.status === ClearanceStatus.APPROVED_WITH_REMARKS) {
          clearance.status = 'ACTIONS_REQUIRED';
        }
      }
    }

    return changed;
  }

  getNumberOfTugboats = (summary: EmergencySummaryExpanded): number | string => {
    let quantity: RequiredQuantity;
    if (isAccessPoint(summary.movementTargetPlace)) {
      return null; // TODO: Don't know where this is saved
    }
    if (isBerth(summary.movementTargetPlace)) {
      quantity = summary.movementTargetPlace.numberOfTugboatsAtArrival;
    }
    if (!quantity && isBerth(summary.movementOriginPlace)) {
      quantity = summary.movementOriginPlace.numberOfTugboatsAtDeparture;
    }
    if (!quantity && isAccessPoint(summary.movementOriginPlace)) {
      return null; // TODO: Don't know where this is saved
    }

    switch (quantity) {
      case RequiredQuantity.ONE:
        return 1;
      case RequiredQuantity.TWO:
        return 2;
      case RequiredQuantity.THREE:
        return 3;
      case RequiredQuantity.FOUR:
        return 4;
      case RequiredQuantity.FIVE:
        return 5;
      case RequiredQuantity.SIX:
        return 6;
      case RequiredQuantity.SEVEN:
        return 7;
      case RequiredQuantity.EIGHT:
        return 8;
      case RequiredQuantity.NINE:
        return 9;
      case RequiredQuantity.PILOT_DETERMINES:
        return "LAB";
      default:
        return null;
    }
  }
}

export interface OperationalVisitEditorComponentData {
  summary: EmergencySummaryExpanded;
}
